import React, { useContext, useEffect, useState } from 'react';
import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { AddButton } from '../components/AddButton';
import { StyledColumnBox, StyledDashboardHeading, StyledRowBox } from '../theme/StyledElements';
import { MessagesDeProducteurs } from '../models/MessagesDeProducteurs';
import { AddMessageModal } from '../components/Message/AddMessageModal';
import { UserContext } from '../providers/UserProvider';
import {
    deleteCurrentMessage,
    getCurrentMessage,
    messageCountCurrentMonth
} from '../services/cloudFirestore';
import { DeleteModal } from '../components/DeleteModal';
import { CardComponent } from '../components/CardComponent';
import { StatisticsCard } from '../components/StatisticsCard';
import { MessageDescriptionComponent } from '../components/Message/MessageDescriptionComponent';
import { convertSecondsToHours } from '../utils';
import { MessageDetailCard } from '../components/Message/MessageDetailCard';
import { MessageTimerComponent } from '../components/Message/MessageTimerComponent';
import { MessageAlert } from '../components/Message/MessageAlert';
import { useMountedRef } from '../hooks/useMountedRef';

export function PushMessage(): React.ReactElement {
    const { t } = useTranslation();
    const user = useContext(UserContext);
    const mounted = useMountedRef();
    const [successMessage, setSuccessMessage] = useState<boolean>(false);
    const [openAddMessage, setOpenAddMessage] = useState<boolean>(false);
    const [openOnceModal, setOpenOnceModal] = useState<boolean>(false);
    const [openThriceModal, setOpenThriceModal] = useState<boolean>(false);
    const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);
    const [timer, setTimer] = useState<number[]>([]);
    const [messageData, setMessageData] = useState<MessagesDeProducteurs>({
        idMessagesDeProducteur: undefined,
        idProducteur: undefined,
        titre: undefined,
        description: undefined,
        numberOfViews: undefined,
        photos: undefined,
        createdDate: undefined
    });

    useEffect(() => {
        if (user && user.idProducteur && mounted.current) {
            getCurrentMessage(user.idProducteur).then((data) => {
                if (data && data.createdDate && mounted.current) {
                    setMessageData(data);
                    setTimer(convertSecondsToHours(data.createdDate));
                }
            });
        }
    }, []);

    const deleteMessage = async (): Promise<void> => {
        if (messageData.idMessagesDeProducteur) {
            await deleteCurrentMessage(messageData.idMessagesDeProducteur);
            window.location.reload();
        }
    };

    const handleAddMessage = async (): Promise<void> => {
        if (messageData.idMessagesDeProducteur) {
            setOpenOnceModal(true);
        } else if (
            user &&
            user.idProducteur &&
            (await messageCountCurrentMonth(user.idProducteur)) >= 3
        ) {
            setOpenThriceModal(true);
        } else {
            setOpenAddMessage(true);
        }
    };

    return (
        <Box sx={{ margin: '75px 0px 80px 40px' }}>
            <MessageAlert
                isModelOpen={successMessage}
                setModelOpen={setSuccessMessage}
                description={t('messagePublishedToAmoUsers')}
                onButtonClick={(): void => {
                    setSuccessMessage(false);
                    window.location.reload();
                }}
            />
            <MessageAlert
                isModelOpen={openOnceModal}
                setModelOpen={setOpenOnceModal}
                description={t('onlyOneMessageAtATime')}
                onButtonClick={(): void => {
                    setOpenOnceModal(false);
                }}
            />
            <MessageAlert
                isModelOpen={openThriceModal}
                setModelOpen={setOpenThriceModal}
                description={t('onlyThreePerMonth')}
                onButtonClick={(): void => {
                    setOpenThriceModal(false);
                }}
            />
            <DeleteModal
                isModelOpen={openDeleteModal}
                setModelOpen={setOpenDeleteModal}
                title={t('deleteMessage')}
                description1={t('confirmDeleteMessage1')}
                description2={t('confirmDeleteMessage2')}
                onCancelClick={(): void => {
                    setOpenDeleteModal(false);
                }}
                onDeleteClick={(): void => {
                    setOpenDeleteModal(false);
                    deleteMessage();
                }}
            />
            <AddMessageModal
                isModelOpen={openAddMessage}
                setModelOpen={setOpenAddMessage}
                onCancelClick={(): void => {
                    setOpenAddMessage(false);
                }}
                onPublishClick={(): void => {
                    setOpenAddMessage(false);
                    setSuccessMessage(true);
                }}
            />
            <AddButton
                disabled={false}
                title={t('passMessage')}
                onButtonClick={(): Promise<void> => handleAddMessage()}
                sx={{
                    width: '200px',
                    borderRadius: '60px',
                    height: '60px'
                }}
            />
            <StyledDashboardHeading variant="h3" sx={{ marginTop: '70px' }}>
                {t('yourCurrentMessage')}
            </StyledDashboardHeading>
            {messageData.idMessagesDeProducteur && (
                <StyledRowBox sx={{ marginTop: '20px' }}>
                    <StyledColumnBox>
                        <CardComponent cardStyle={{ height: 'auto', marginBottom: '20px' }}>
                            <MessageTimerComponent
                                timer={timer}
                                onDeleteClick={(): void => setOpenDeleteModal(true)}
                            />
                        </CardComponent>
                        <StatisticsCard
                            statistics={messageData.numberOfViews ?? 0}
                            title={t('views')}
                        />
                    </StyledColumnBox>
                    <StyledColumnBox>
                        <MessageDescriptionComponent messageData={messageData} user={user} />
                        <MessageDetailCard messageData={messageData} user={user} />
                    </StyledColumnBox>
                </StyledRowBox>
            )}
        </Box>
    );
}
